import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { login, clear2FAState, setCredentials } from '@/store/slices/authSlice'
import authService from '@/services/authService'

const LoginPage = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isAuthenticated, loading, error, twoFactorRequired, twoFactorTempToken } = useSelector((state) => state.auth)

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  })

  // 2FA state
  const [otpCode, setOtpCode] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)
  const [recoveryCode, setRecoveryCode] = useState('')
  const [twoFALoading, setTwoFALoading] = useState(false)
  const [twoFAError, setTwoFAError] = useState('')

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true })
    }
  }, [isAuthenticated, navigate])

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      await dispatch(login(formData)).unwrap()
      // If twoFactorRequired is set in Redux, the useEffect above won't fire
      // since isAuthenticated remains false. The render will show the 2FA form.
    } catch (err) {
      console.error('Login error:', err)
    }
  }

  const handle2FASubmit = async (e) => {
    e.preventDefault()
    setTwoFAError('')
    setTwoFALoading(true)

    try {
      const res = await authService.verify2FA(
        twoFactorTempToken,
        useRecovery ? undefined : otpCode,
        useRecovery ? recoveryCode : undefined
      )

      const data = res.data?.data
      if (!data || !data.access_token) {
        throw new Error('驗證回應異常')
      }

      // Dispatch setCredentials to store tokens and user in Redux + localStorage
      dispatch(setCredentials({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        user: data.user,
      }))

      navigate('/', { replace: true })
    } catch (err) {
      const msg = err.response?.data?.message || err.message || '驗證失敗，請重試'
      setTwoFAError(msg)
    } finally {
      setTwoFALoading(false)
    }
  }

  const handleCancelTwoFA = () => {
    dispatch(clear2FAState())
    setOtpCode('')
    setRecoveryCode('')
    setTwoFAError('')
    setUseRecovery(false)
  }

  // Render 2FA verification form when login returned 2fa_required
  if (twoFactorRequired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">雙因素驗證</h1>
            <p className="text-gray-500 text-sm">
              {useRecovery
                ? '請輸入您的恢復碼（格式：XXXX-XXXX-XXXX）'
                : '請開啟驗證器 App，輸入 6 位數驗證碼'}
            </p>
          </div>

          {twoFAError && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {twoFAError}
            </div>
          )}

          <form onSubmit={handle2FASubmit} className="space-y-5">
            {useRecovery ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">恢復碼</label>
                <input
                  type="text"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                  placeholder="XXXX-XXXX-XXXX"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition text-center tracking-widest font-mono text-lg"
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">驗證碼</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  required
                  autoFocus
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition text-center tracking-widest font-mono text-2xl"
                />
                <p className="text-xs text-gray-400 mt-1 text-center">每 30 秒更新一次</p>
              </div>
            )}

            <button
              type="submit"
              disabled={twoFALoading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {twoFALoading ? '驗證中...' : '確認登入'}
            </button>
          </form>

          <div className="mt-5 text-center space-y-2">
            <button
              type="button"
              onClick={() => { setUseRecovery(!useRecovery); setTwoFAError('') }}
              className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
            >
              {useRecovery ? '改用驗證器 App' : '改用恢復碼登入'}
            </button>
            <div>
              <button
                type="button"
                onClick={handleCancelTwoFA}
                className="text-sm text-gray-500 hover:text-gray-700 hover:underline"
              >
                取消，返回登入
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Normal login form
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            智能出勤與人事管理系統
          </h1>
          <p className="text-gray-600">請登入您的帳號</p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              使用者名稱
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              placeholder="請輸入使用者名稱"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              密碼
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              placeholder="請輸入密碼"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? '登入中...' : '登入'}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600">
          <p>忘記密碼？ <Link to="/forgot-password" className="text-blue-600 hover:text-blue-700">重設密碼</Link></p>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
